import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useState, useEffect } from 'react';
import { getSettings } from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_84027868-605e-4a17-95ea-ac9185a5f86d/artifacts/v7atgd7h_Impulsodigital2030%40gmail.com_20260410_215902_0000.png";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useLang();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  return (
    <footer id="contact" className="bg-[#050505] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Ferr Tours" className="h-16 w-auto" />
            </Link>
            <p className="text-slate-400 mb-4">{t('foot_desc')}</p>
            <p className="text-cyan-400 text-sm font-medium">{t('foot_slogan')}</p>
          </div>

          <div>
            <h3 className="text-white font-bold mb-6 uppercase tracking-wider">{t('foot_links')}</h3>
            <ul className="space-y-3">
              <li><a href="/#services" className="text-slate-400 hover:text-cyan-400 transition-colors">{t('nav_services')}</a></li>
              <li><a href="/#destinations" className="text-slate-400 hover:text-cyan-400 transition-colors">{t('nav_destinations')}</a></li>
              <li><a href="/#excursions" className="text-slate-400 hover:text-cyan-400 transition-colors">{t('nav_excursions')}</a></li>
              <li><a href="/#vehicles" className="text-slate-400 hover:text-cyan-400 transition-colors">{t('nav_vehicles')}</a></li>
              <li><a href="/#booking" className="text-slate-400 hover:text-cyan-400 transition-colors">{t('book_now')}</a></li>
              <li><Link to="/admin" className="text-slate-400 hover:text-cyan-400 transition-colors">{t('foot_admin')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-6 uppercase tracking-wider">{t('foot_areas')}</h3>
            <ul className="space-y-3">
              <li className="text-slate-400">Punta Cana / Bávaro</li>
              <li className="text-slate-400">Uvero Alto</li>
              <li className="text-slate-400">Cabeza de Toro</li>
              <li className="text-slate-400">Bayahibe</li>
              <li className="text-slate-400">Santo Domingo</li>
              <li className="text-slate-400">La Romana</li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-6 uppercase tracking-wider">{t('foot_contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                <span className="text-slate-400">Punta Cana, Dominican Republic</span>
              </li>
              <li>
                <a href="https://wa.me/18299838580" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-green-400 transition-colors" data-testid="footer-whatsapp">
                  <Phone className="w-5 h-5 text-green-400 flex-shrink-0" />+1 (829) 983-8580
                </a>
              </li>
              <li>
                <a href="mailto:ferrtour@gmail.com" className="flex items-center gap-3 text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-email">
                  <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />ferrtour@gmail.com
                </a>
              </li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a href="https://wa.me/18299838580" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center hover:bg-green-500/30 transition-colors" data-testid="footer-social-whatsapp">
                <svg viewBox="0 0 32 32" className="w-5 h-5 fill-current"><path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958C9.72 31.058 12.738 32 16.004 32 24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.335 22.594c-.39 1.1-1.932 2.014-3.182 2.28-.856.182-1.974.326-5.738-1.234-4.818-1.994-7.92-6.878-8.162-7.196-.232-.318-1.952-2.6-1.952-4.958s1.234-3.516 1.672-3.996c.39-.428 1.028-.624 1.64-.624.198 0 .376.01.536.018.438.018.658.044.948.734.362.862 1.246 3.042 1.354 3.264.11.222.184.48.036.77-.138.3-.208.486-.414.748-.208.264-.436.588-.624.79-.208.24-.424.498-.182.976.242.478 1.078 1.778 2.314 2.88 1.59 1.416 2.93 1.854 3.348 2.06.418.208.662.174.906-.104.252-.286 1.078-1.254 1.366-1.684.28-.43.566-.356.952-.214.39.144 2.466 1.164 2.89 1.376.422.214.704.318.808.498.102.178.102 1.04-.288 2.14z"/></svg>
              </a>
              {settings.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center hover:bg-blue-500/30 transition-colors" data-testid="footer-social-facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {settings.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-pink-500/20 text-pink-400 flex items-center justify-center hover:bg-pink-500/30 transition-colors" data-testid="footer-social-instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">© {currentYear} Ferr Tours. {t('foot_rights')}</p>
          <p className="text-slate-500 text-sm">{t('foot_tagline')}</p>
        </div>
      </div>
    </footer>
  );
}
